import { Claim } from 'types/claim';
import { Order } from 'types/order';
import { StoreAnalyticsInterface } from 'types/store';
import { getStoreCurrencyFormatter } from 'utils';

export const todos = [
  {
    title: 'Create Your Company Account',
    to: '/settings/team',
    key: 'companyUpdate',
  },
  {
    title: 'Set Your Delivery Guarantee Preferences',
    to: '/settings/protection',
    key: 'protection',
  },
  {
    title: 'Set Customer Portal preferences',
    to: '/settings/customer',
    key: 'customer',
  },
  {
    title: 'Set Notifications',
    to: '/settings/notifications',
    key: 'notifications',
  },
  {
    title: 'Set Tracking Templates',
    to: '/settings/tracking-templates',
    key: 'trackingPage',
  },
  {
    title: 'Invite Your Team',
    to: '/settings/team',
    key: 'teamInvite',
  },
];

export const formatRevenueAndClaimsCost = (
  data: any,
  currency: string,
  storeAnalytics = {},
) => {
  const protectionRevenue = (
    (storeAnalytics as any)?.protectedOrders?.nodes || []
  ).reduce((total: number, order: Order) => {
    return total + (Number(order?.protectionTotal) || 0);
  }, 0);

  const claimCost = (
    Array.isArray(data?.claimsPaid) ? data.claimsPaid : []
  ).reduce((total: number, claim: Claim) => {
    return total + (Number(claim?.total) || 0);
  }, 0);

  return [
    {
      name: 'Delivery Guarantee Revenue',
      currentStat: getStoreCurrencyFormatter(currency, protectionRevenue),
      link: {
        text: 'Manage Issues',
        url: '/claims',
        classes: 'text-blue-700 text-right',
      },
    },
    {
      name: 'ISSUES COST',
      currentStat: getStoreCurrencyFormatter(currency, claimCost),
      link: {
        text: 'Manage Issues',
        url: '/claims',
        classes: 'text-blue-700 text-right',
      },
    },
  ];
};

export const dashboardStats = (
  storeAnalytics: StoreAnalyticsInterface,
  barData: any,
  storeCurrency: string = 'USD',
) => {
  const protectedRevenueAndClaimsCost = barData
    ? formatRevenueAndClaimsCost(barData, storeCurrency, storeAnalytics)
    : [];
  const stats = [
    ...protectedRevenueAndClaimsCost,
    {
      name: 'ORDERS EDITED',
      currentStat:
        `${storeAnalytics?.totalEditedOrders?.aggregate?.total ?? 0}` || '0',
      link: {
        text: 'View Edited Orders',
        url: '/orders?orderEdited=Yes',
        classes: 'text-blue-700 text-right',
      },
    },
    {
      name: 'TOTAL ORDERS',
      currentStat:
        `${storeAnalytics?.totalOrders?.aggregate?.total ?? 0}` || '0',
      link: {
        text: 'View Orders',
        url: '/orders',
        classes: 'text-blue-700 text-right',
      },
    },
    {
      name: 'Guaranteed',
      currentStat:
        `${storeAnalytics?.protectedOrders?.aggregate?.total ?? 0}` || '0',
      link: {
        text: 'Manage',
        url: '/orders',
        classes: 'text-blue-700 text-right',
      },
    },
    {
      name: 'Not guaranteed',
      currentStat:
        `${storeAnalytics?.unprotectedOrders?.aggregate?.total ?? 0}` || '0',
      link: {
        text: 'Manage Issues',
        url: '/claims',
        classes: 'text-blue-700 text-right',
      },
    },
    {
      name: 'TOTAL ISSUES',
      currentStat:
        `${storeAnalytics?.totalClaims?.aggregate?.count ?? 0}` || '0',
      link: {
        text: 'Manage Issues',
        url: '/claims',
        classes: 'text-blue-700 text-right',
      },
    },
    {
      name: 'NEW ISSUES',
      currentStat: `${storeAnalytics?.newClaims?.aggregate?.count ?? 0}` || '0',
      link: {
        text: 'Manage Issues',
        url: '/claims',
        classes: 'text-blue-700 text-right',
      },
    },
    {
      name: 'ISSUES IN REVIEW',
      currentStat:
        `${storeAnalytics?.reviewClaims?.aggregate?.count ?? 0}` || '0',
      link: {
        text: 'Manage Issues',
        url: '/claims',
        classes: 'text-blue-700 text-right',
      },
    },
    {
      name: 'RESOLVED ISSUES',
      currentStat:
        `${storeAnalytics?.resolvedClaims?.aggregate?.count ?? 0}` || '0',
      link: {
        text: 'Manage Issues',
        url: '/claims',
        classes: 'text-blue-700 text-right',
      },
    },
    {
      name: 'DECLINED ISSUES',
      currentStat:
        `${storeAnalytics?.deniedClaims?.aggregate?.count ?? 0}` || '0',
      link: {
        text: 'Manage Issues',
        url: '/claims',
        classes: 'text-blue-700 text-right',
      },
    },
  ];
  return stats;
};

const getAggregateTotal = (value: any) => Number(value?.aggregate?.total) || 0;
const getAggregateCount = (value: any) => Number(value?.aggregate?.count) || 0;

const sumProtectionRevenue = (storeAnalytics?: StoreAnalyticsInterface) => {
  const protectedOrderNodes = storeAnalytics?.protectedOrders?.nodes;
  const protectedOrders = Array.isArray(protectedOrderNodes)
    ? protectedOrderNodes
    : [];

  return protectedOrders.reduce((total: number, order: any) => {
    return total + (Number(order?.protectionTotal) || 0);
  }, 0);
};

const sumClaimsCost = (barData: any) => {
  const claimsPaid = Array.isArray(barData?.claimsPaid)
    ? barData.claimsPaid
    : [];

  return claimsPaid.reduce((total: number, claim: Claim) => {
    return total + (Number(claim?.total) || 0);
  }, 0);
};

export const getDashboardMetricSummary = (
  storeAnalytics?: StoreAnalyticsInterface,
  barData?: any,
  storeCurrency?: string | null,
) => {
  const currency = storeCurrency || 'USD';
  const totalOrders = getAggregateTotal(storeAnalytics?.totalOrders);
  const protectedOrders = getAggregateTotal(storeAnalytics?.protectedOrders);
  const unprotectedOrders = getAggregateTotal(
    storeAnalytics?.unprotectedOrders,
  );
  const protectionRevenue = sumProtectionRevenue(storeAnalytics);
  const issueCost = sumClaimsCost(barData);
  const netAfterIssueCost = protectionRevenue - issueCost;
  const attachmentRate =
    totalOrders > 0
      ? Math.round((protectedOrders / totalOrders) * 1000) / 10
      : 0;
  const averageGuarantee =
    protectedOrders > 0 ? protectionRevenue / protectedOrders : 0;

  return {
    totalOrders,
    protectedOrders,
    unprotectedOrders,
    totalIssues: getAggregateCount(storeAnalytics?.totalClaims),
    newIssues: getAggregateCount(storeAnalytics?.newClaims),
    reviewIssues: getAggregateCount(storeAnalytics?.reviewClaims),
    resolvedIssues: getAggregateCount(storeAnalytics?.resolvedClaims),
    declinedIssues: getAggregateCount(storeAnalytics?.deniedClaims),
    protectionRevenue,
    issueCost,
    netAfterIssueCost,
    attachmentRate,
    averageGuarantee,
    formattedProtectionRevenue: getStoreCurrencyFormatter(
      currency,
      protectionRevenue,
    ),
    formattedIssueCost: getStoreCurrencyFormatter(currency, issueCost),
    formattedNetAfterIssueCost: getStoreCurrencyFormatter(
      currency,
      netAfterIssueCost,
    ),
    formattedAverageGuarantee: getStoreCurrencyFormatter(
      currency,
      averageGuarantee,
    ),
  };
};

export const protectedOrdersData = [
  { name: 'Guaranteed', value: 2400, fill: '#2564eb' },
  { name: 'Not guaranteed', value: 1398, fill: '#5288ff' },
];

export const revenueData = [
  {
    name: '2/3/22',
    orders: 4000,
    claims: 1000,
  },
  {
    name: '3/3/22',
    orders: 3000,
    claims: 2000.5,
  },
  {
    name: '4/3/22',
    orders: 3500,
    claims: 500,
  },
  {
    name: '5/3/22',
    orders: 200,
    claims: 450,
  },
  {
    name: '6/3/22',
    orders: 3500,
    claims: 3500,
  },
];

export const claimsFiledData = [
  {
    name: '2/3/22',
    number: 400,
  },
  {
    name: '3/3/22',
    number: 300,
  },
  {
    name: '4/3/22',
    number: 550,
  },
  {
    name: '5/3/22',
    number: 250,
  },
  {
    name: '6/3/22',
    number: 350,
  },
];

export const rateData = [
  {
    name: '2/3/22',
    amount: 4000,
  },
  {
    name: '3/3/22',
    amount: 3000,
  },
  {
    name: '4/3/22',
    amount: 5500,
  },
  {
    name: '5/3/22',
    amount: 2500,
  },
  {
    name: '6/3/22',
    amount: 3500,
  },
];
